// Resolving which prices `/premium` sells.
//
// These are pinned to Stripe `lookup_key`s rather than discovered by listing
// the product's prices. A recency window (`limit: 2`, newest-first) meant any
// price added in the Stripe dashboard silently became the price the site
// charged — that is how $10/mo · $100/yr shipped to every visitor on
// 2026-07-21 without a deploy. A lookup key names one intended price, so a
// deliberate reprice is a key transfer in Stripe and an accidental new price
// is invisible here.
import type Stripe from 'stripe';

const PREMIUM_PRICE_LOOKUP_KEYS = Object.freeze({
  MONTH: 'premium_monthly',
  YEAR: 'premium_annual',
});

export interface ResistbotPrice {
  id: string;
  unitAmount: number | null;
  formattedAmount: string;
}

export interface ResistbotPrices {
  month: ResistbotPrice;
  year: ResistbotPrice;
}

/** The slice of the Stripe client this module needs, so tests can pass a fake. */
export interface PriceLister {
  prices: {
    list: (params: Stripe.PriceListParams) => Promise<{
      data: Stripe.Price[];
    }>;
  };
}

const normalizePrice = (price: Stripe.Price): ResistbotPrice => ({
  id: price.id,
  unitAmount: price.unit_amount,
  formattedAmount: `${(price.unit_amount ?? 0) / 100}`,
});

/** Look up the monthly and annual premium prices by their Stripe lookup keys.
 * Throws if either key is missing or resolves to more than one active price,
 * so a misconfigured Stripe fails loudly instead of charging the wrong amount. */
const resolvePremiumPrices = async (
  client: PriceLister
): Promise<ResistbotPrices> => {
  const lookupKeys = [
    PREMIUM_PRICE_LOOKUP_KEYS.MONTH,
    PREMIUM_PRICE_LOOKUP_KEYS.YEAR,
  ];

  const resp = await client.prices.list({
    lookup_keys: lookupKeys,
    active: true,
    limit: lookupKeys.length,
  });

  const byLookupKey = new Map<string, Stripe.Price>();
  resp.data.forEach((price) => {
    if (!price.lookup_key) {
      return;
    }
    if (byLookupKey.has(price.lookup_key)) {
      throw new Error(
        `Multiple active prices share the lookup key ${price.lookup_key}`
      );
    }
    byLookupKey.set(price.lookup_key, price);
  });

  const month = byLookupKey.get(PREMIUM_PRICE_LOOKUP_KEYS.MONTH);
  const year = byLookupKey.get(PREMIUM_PRICE_LOOKUP_KEYS.YEAR);

  if (!month || !year) {
    const missing = lookupKeys.filter((key) => !byLookupKey.has(key));
    throw new Error(
      `No active Stripe price for lookup key(s): ${missing.join(', ')}`
    );
  }

  return {
    month: normalizePrice(month),
    year: normalizePrice(year),
  };
};

export { PREMIUM_PRICE_LOOKUP_KEYS, resolvePremiumPrices };
